import React, {useEffect, useState, useContext, useCallback} from "react";
import {injectIntl} from "react-intl";
import AisleLabel from "./AisleLabel";
import Bin from "./Bin";
import ProvisioningStatus from "./ProvisioningStatus";
import BinColorLegend from "./BinColorLegend";
import {BRANCH, LEAF} from "../../../util/Constants";
import {formatAisleLabel, getMqttTopicForLidarTester, getSortedAssetByAisle,
    getExhaustiveLabelSet, determineMergeDirection, calculateSensorStatesByType,
    getBranchIndexForAssetMapping} from "../../../util/LidarTesterUtil";
import {LidarTesterContext} from "../context/LidarTesterContext";
import MQTTUtility from "../../../util/MQTTUtility";
import {ProvisioningContext} from "../context/ProvisioningContext";
import {getMQTTClientConfig, getNodeId, isSensorLevelConfigEnabledForLidarController} from "../../../util/Util";
import ColumnLabelRow from "./ColumnLabelRow";
import Alert from "@amzn/meridian/alert";
import {getLocalizedMessage} from "../../../util/Localization";

const GRID_STYLE = {
    display: "flex"
}

const LidarTesterGridView = (props) => {

    const {device, selectedMac, intl, cartRecipesMap} = props;
    const {testerViewModel, testerDispatch} = useContext(LidarTesterContext);
    const [error, setError] = useState(false);
    const {provisioningViewModel, provisioningDispatch} = useContext(ProvisioningContext);
    const [isSubscribing, setIsSubscribing] = useState(false);

    // Extract values from provisioningViewModel with default values
    const provisioningTriggered = provisioningViewModel ? provisioningViewModel.provisioningTriggered : false;
    const provisioningData = provisioningViewModel ? provisioningViewModel.provisioningData : [];
    const clusterStatus = provisioningViewModel ? provisioningViewModel.clusterStatus : null;

    // Memoize derived values to prevent unnecessary recalculations
    const sortedAssetByAisle = React.useMemo(() =>
        device ? getSortedAssetByAisle(device) : null
        , [device]);

    const exhaustiveLabelSet = React.useMemo(() =>
        device ? getExhaustiveLabelSet(device, cartRecipesMap) : null
        , [device, cartRecipesMap]);

    const isSensorLevelConfigEnabled = React.useMemo(() =>
        device ? isSensorLevelConfigEnabledForLidarController(device) : false
        , [device]);

    // Load state map only when device changes or when component mounts
    useEffect(() => {
        if (device && testerDispatch) {
            testerDispatch({
                type: "LOAD_STATE_MAP", data: {
                    sortedAssetByAisle,
                    dispatcher: testerDispatch,
                    macAddress: device.mac,
                    columnsList: exhaustiveLabelSet,
                    isSensorLevelConfigEnabled,
                    cartRecipesMap
                }
            });
        }
    }, [device, testerDispatch]) // Only depend on device and testerDispatch

    /**
     * Checks if the message is a provisioning message
     * @param {Object} data - The parsed message data
     * @returns {boolean} - Whether it's a provisioning message
     */
    const isProvisioningMessage = useCallback((data) => {
        return data && typeof data.nodeType === 'string' &&
            (data.nodeType === BRANCH || data.nodeType === LEAF);
    }, []);

    /**
     * Checks if the message is a LidarTester message
     * @param {Object} data - The parsed message data
     * @returns {boolean} - Whether it's a LidarTester message
     */
    const isLidarTesterMessage = useCallback((data) => {
        return data &&
            typeof data.aisleLabel === 'string' &&
            typeof data.columnLabel === 'string' &&
            data.address !== undefined &&
            data.sensorIndex !== undefined;
    }, []);

    /**
     * Handles provisioning messages
     * @param {Object} data - The parsed message data
     */
    const handleProvisioningMessage = useCallback((data) => {
        // Always process provisioning messages to maintain separation of concerns
        // The ProvisioningReducer will handle its own state management
        provisioningDispatch({
            type: "UPDATE_PROVISIONING_DATA",
            data: {message: data}
        });
    }, [provisioningDispatch]);

    /**
     * Handles LidarTester messages
     * @param {Object} data - The parsed message data
     */
    const handleLidarTesterMessage = useCallback((data) => {
        // Always process LidarTester messages to maintain separation of concerns
        // The LidarTesterReducer will handle its own state management
        testerDispatch({
            type: "UPDATE_STATE_MAP",
            data: {dispatcher: testerDispatch, message: data}
        });
    }, [testerDispatch]);

    /**
     * Handles message processing errors
     * @param {Error} error - The error that occurred
     * @param {Object} message - The raw message
     */
    const handleMessageError = useCallback((error, message) => {
        console.error("Error processing MQTT message:", error);
        setError(true);
    }, [setError]);

    // MQTT message handler
    const onMessage = useCallback((message) => {
        try {
            const messageStr = message.toString();
            const parsedData = JSON.parse(messageStr);

            // Determine message type and route to appropriate handler
            if (isProvisioningMessage(parsedData)) {
                handleProvisioningMessage(parsedData);
            } else if (isLidarTesterMessage(parsedData)) {
                // Don't log every LidarTester message to reduce noise
                handleLidarTesterMessage(parsedData);
            } else {
                console.log("Received unknown message format, ignoring", parsedData);
            }
        } catch (error) {
            handleMessageError(error, message);
        }
    }, [isProvisioningMessage, isLidarTesterMessage, handleProvisioningMessage,
        handleLidarTesterMessage, handleMessageError]);

    // MQTT props for the subscription
    const mqttUtilityProps = useCallback(() => {
        if (!device || !device.ip) return null;
        return {
            topic: getMqttTopicForLidarTester(getNodeId(), device.ip),
            config: getMQTTClientConfig(),
            onMessage,
            setError,
            setIsSubscribing,
            isSubscribing
        };
    }, [device, onMessage, setError, setIsSubscribing, isSubscribing]);

    /**
     * Generates props for Bin components with sensor states and merge information
     * 
     * @param {string} aisleLabel - Formatted aisle label (e.g., "A-1")
     * @param {string} label - Column label (e.g., "A", "TU", "JK")
     * @param {number} binIndex - Index in exhaustiveLabelSet for merge detection
     * @returns {Object} Props object for Bin component
     * 
     * @example
     * getBinColumnProps("A-1", "TU", 2)
     * Returns: { sensorStates: [...], isAnomaly: false, columnLabel: "TU", shouldMergeRight: false, shouldMergeLeft: false }
     */
    const getBinColumnProps = (aisleLabel, label, binIndex) => {
        const isSensorLevelConfigEnabled = isSensorLevelConfigEnabledForLidarController(device);
        
        // Determine merge directions based on adjacent columns
        const { shouldMergeRight, shouldMergeLeft } = determineMergeDirection({
            exhaustiveLabelSet,
            binIndex,
            device,
            aisleLabel
        });
        
        // Calculate sensor states based on column type and merge state
        const { sensorStates, isAnomaly } = calculateSensorStatesByType({
            testerViewModel,
            aisleLabel,
            label,
            isSensorLevelConfigEnabled,
            shouldMergeRight,
            shouldMergeLeft
        });
        
        return {
            sensorStates,
            isAnomaly,
            columnLabel: label,
            shouldMergeRight,
            shouldMergeLeft,
            aisleLabel,
            device
        };
    };

    return (
        <div>
            {selectedMac && <MQTTUtility {...mqttUtilityProps()}/>}
            {error && <Alert type="error">{getLocalizedMessage({intl, stringId: 'log.error'})}</Alert>}
            
            {exhaustiveLabelSet && (
                <div style={GRID_STYLE}>
                    <ColumnLabelRow {...{labelList: exhaustiveLabelSet}}/>
                    <div style={{
                        width: "200px",
                        height: "25px",
                        textAlign: "center",
                        fontWeight: "bold",
                        visibility: provisioningTriggered ? 'visible' : 'hidden',
                        minWidth: "200px", // Ensure consistent width
                        backgroundColor: 'transparent',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 'none'
                    }}>
                        Provisioning
                    </div>
                </div>
            )}
            {sortedAssetByAisle && sortedAssetByAisle.map((assetMapping, index) => {
                const branchIndex = getBranchIndexForAssetMapping(assetMapping);
                return (
                    <div key={`aisle-row-${assetMapping.aisleLabel}-${index}`} style={GRID_STYLE}>
                        <AisleLabel {...{aisleLabel: assetMapping.aisleLabel}}/>
                        {exhaustiveLabelSet && exhaustiveLabelSet.map((label, binIndex) =>
                            <Bin key={`bin-${assetMapping.aisleLabel}-${label}-${binIndex}`} {...getBinColumnProps(formatAisleLabel(assetMapping.aisleLabel), label, binIndex)}/>)}
                        <ProvisioningStatus
                            isProvisioned={provisioningTriggered && branchIndex !== null && provisioningData.some(item =>
                                item.nodeType === LEAF &&
                                item.parentNodeIndex === branchIndex &&
                                item.expectedNodeCount === item.detectedNodeCount
                            )}
                            provisioningData={provisioningTriggered && branchIndex !== null ? provisioningData.find(item =>
                                item.nodeType === LEAF &&
                                item.parentNodeIndex === branchIndex
                            ) : null}
                            visible={provisioningTriggered}
                            clusterStatus={clusterStatus}
                            key={`prov-status-${assetMapping.aisleLabel}`}
                        />
                    </div>
                )
            })}

            {/* Add spacing between the grid and the legends*/}
            <div style={{marginTop: '30px'}}></div>
            {/* Bin Color Legend */}
            {selectedMac && <BinColorLegend />}
            {/* Add spacing at footer */}
            <div style={{marginTop: '40px'}}></div>
        </div>
    )
}

export default injectIntl(LidarTesterGridView);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/lidar/lidarTester/LidarTesterGridView.js